import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/api';
import { installAuthInterceptor } from '@/api/authInterceptor';
import { TooltipProvider } from '@/components/ui/Tooltip';
import { ThemeProvider } from '@/components/ThemeProvider';
import { PwaUpdater } from '@/pwa/PwaUpdater';
import { routeTree } from './routeTree.gen';
import './styles/index.css';

// Wire the 401-handler middleware before the first React render.
// See src/api/auth-interceptor.ts and
// apps/client/docs/overview.md §"Auth surface".
installAuthInterceptor();

/**
 * The router is built once at module load with the QueryClient threaded
 * into context. Routes can read `useRouteContext().queryClient` and use
 * loaders to prefetch into the same cache that components consume via
 * `$api.useQuery(...)`.
 *
 *   defaultPreload: 'intent'   — preload route data on link hover/focus.
 *   scrollRestoration: true    — restore scroll position on back/forward.
 */
const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  scrollRestoration: true,
});

/**
 * Module augmentation so `useRouter()`, `Link`, and friends are typed
 * against this concrete router instance — typos in route paths or
 * search params surface as compile errors.
 */
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Missing #root element in index.html');
}

createRoot(rootEl).render(
  <StrictMode>
    {/*
      ThemeProvider sits outermost so every themed surface (the whole router
      tree) reads from the same theme context, and so the `.dark` class it
      toggles on <html> is in place before any descendant renders. The
      pre-paint half lives as an inline script in index.html (FOUC
      prevention) — this provider takes over reactive control after hydration.
      See components/theme-provider.tsx.
    */}
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        {/*
          TooltipProvider sits once at the app root so every route descendant
          shares the same `skipDelayDuration` — once a user hovers one tooltip,
          the next ones reveal faster. `delayDuration={0}` (the default we set
          in `components/ui/tooltip.tsx`) keeps the reveal instant; individual
          `<Tooltip>` instances can override per-consumer. See
          `design/components/tooltip.md` →  "Provider placement."
        */}
        <TooltipProvider>
          <RouterProvider router={router} />
          {/*
            PwaUpdater registers the service worker (CSP-safe, in bundled JS —
            see src/pwa/PwaUpdater.tsx) and renders the "new version available"
            prompt as a fixed overlay above the whole app. It sits as a sibling
            of the router so the prompt is route-independent.
          */}
          <PwaUpdater />
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
